import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { LEARNING_HUB_MODULE_PATH, LEARNING_HUB_SEARCH_PATH } from '../../definitions/learning-hub.constants';
import { LearningHubLanguage } from '../../definitions/learning-hub.models';

/**
 * Search field (@rappi/ui `one-input type="search"`, the search input of Brands by Rappi) with a submit button.
 * By default a submit opens /learning-hub/search?lang=..&q=..; with `live` it emits `queryChange` while typing
 * (debounced) and on submit instead.
 */
@Component({
  selector: 'app-learning-hub-search-box',
  templateUrl: './search-box.component.html',
  styleUrls: ['./search-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchBoxComponent implements OnInit, OnChanges, OnDestroy {
  @Input() lang: LearningHubLanguage;
  @Input() query = '';
  @Input() size: 'large' | 'compact' = 'compact';
  @Input() live = false;
  @Output() queryChange = new EventEmitter<string>();

  readonly control = new FormControl('');
  private readonly DESTROY$ = new Subject<void>();

  constructor(private readonly _router: Router) {}

  ngOnInit(): void {
    this.control.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.DESTROY$))
      .subscribe((value: string) => {
        if (this.live) {
          this.queryChange.emit((value || '').trim());
        }
      });
  }

  ngOnChanges(): void {
    if ((this.control.value || '') !== (this.query || '')) {
      this.control.setValue(this.query || '', { emitEvent: false });
    }
  }

  ngOnDestroy(): void {
    this.DESTROY$.next();
    this.DESTROY$.complete();
  }

  submit(event?: Event): void {
    event?.preventDefault();
    const query = (this.control.value || '').trim();
    if (this.live) {
      this.queryChange.emit(query);
      return;
    }
    this._router.navigate(['/', LEARNING_HUB_MODULE_PATH, LEARNING_HUB_SEARCH_PATH], {
      queryParams: { lang: this.lang, q: query || null }
    });
  }
}
